<template>
    <ul
        ref="itemListEl"
        class="item-list"
        :style="{
            display: isOpen ? 'block' : 'none',
            'max-height': MAX_LIST_HEIGHT + 'px',
        }"
        :class="[isOpen ? 'expanded' : '']"
    >
        <li
            v-for="(item, index) in itemList"
            :key="item.key"
            :class="[index === currentHighlight ? 'active' : '']"
            @mouseenter="setHighlighted(index, false)"
            @mousemove="setHighlighted(index, false)"
            @click.stop="click(item)"
        >
            <span
                v-if="showFlags"
                :class="`country-flag fi fi-${String(item.key).toLowerCase()}`"
            />
            {{ item.value }}
        </li>
    </ul>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    onMounted,
    onBeforeUnmount,
    watch,
    PropType,
} from 'vue';
import type { FieldInterface } from '@/types';

enum Key {
    UP = 'ArrowUp',
    DOWN = 'ArrowDown',
    ENTER = 'Enter',
}

const MAX_LIST_HEIGHT = window.innerHeight < 500 ? 140 : 250;

export default defineComponent({
    name: 'ItemList',
    props: {
        itemList: {
            type: Array as PropType<FieldInterface[]>,
            required: true,
        },
        isDefaultOpen: {
            type: Boolean,
            default: false,
        },
        showFlags: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['selectOption'],
    setup(props, { emit }) {
        const currentHighlight = ref(0);
        const listHeight = ref(0);
        const isOpen = ref(props.isDefaultOpen);
        const itemListEl = ref<HTMLElement | null>(null);

        function click(item: FieldInterface) {
            emit('selectOption', item);
        }

        function enableKeyboardControl() {
            disableKeyboardControl();
            document.addEventListener('keydown', keyboardControl);
        }

        function disableKeyboardControl() {
            document.removeEventListener('keydown', keyboardControl);
        }

        function scrollToHighlighted() {
            if (!itemListEl.value) return;
            const itemList = itemListEl.value;
            if (
                itemList.children.length > 0 &&
                currentHighlight.value < itemList.children.length
            ) {
                const item = itemList.children[
                    currentHighlight.value
                ] as HTMLElement;
                itemList.scrollTo({
                    top: item.offsetTop,
                });
            }
        }

        function setHighlighted(index: number, updateScroll = true) {
            if (index < props.itemList.length && index >= 0) {
                if (
                    currentHighlight.value === undefined ||
                    currentHighlight.value === null
                ) {
                    currentHighlight.value = 0;
                }
                if (!itemListEl.value) return;
                const itemList = itemListEl.value;
                let itemListViewportStart = itemList.scrollTop;
                let itemListViewportEnd =
                    itemList.scrollTop +
                    itemList.getBoundingClientRect().height;
                let highlightedItem = itemList.children[
                    currentHighlight.value
                ] as HTMLElement;
                if (
                    highlightedItem &&
                    updateScroll &&
                    (highlightedItem.offsetTop > itemListViewportEnd ||
                        highlightedItem.offsetTop < itemListViewportStart)
                ) {
                    scrollToHighlighted();
                }
                currentHighlight.value = index;
                if (updateScroll) {
                    itemListViewportStart = itemList.scrollTop;
                    itemListViewportEnd =
                        itemList.scrollTop +
                        itemList.getBoundingClientRect().height;
                    highlightedItem = itemList.children[
                        currentHighlight.value
                    ] as HTMLElement;
                    const highlightedItemHeight =
                        highlightedItem.getBoundingClientRect().height;
                    if (
                        highlightedItem.offsetTop >
                        itemListViewportEnd - highlightedItemHeight
                    ) {
                        itemList.scrollBy(0, highlightedItemHeight);
                    } else if (
                        highlightedItem.offsetTop < itemListViewportStart
                    ) {
                        itemList.scrollBy(0, -highlightedItemHeight);
                    }
                }
            }
        }

        function keyboardControl(evt: KeyboardEvent) {
            if (evt.key === Key.UP || evt.key === Key.DOWN) {
                evt.preventDefault();
            }

            if (
                !currentHighlight.value ||
                !props.itemList[currentHighlight.value]
            ) {
                currentHighlight.value = 0;
            }

            switch (evt.key) {
                case Key.UP:
                    if (currentHighlight.value > 0) {
                        setHighlighted(currentHighlight.value - 1);
                    }
                    break;
                case Key.DOWN:
                    if (currentHighlight.value < props.itemList.length - 1) {
                        setHighlighted(currentHighlight.value + 1);
                    }
                    break;
                case Key.ENTER:
                    evt.preventDefault();
                    emit(
                        'selectOption',
                        props.itemList[currentHighlight.value]
                    );
                    break;
            }
        }

        function getItemNodes(): any {
            return itemListEl.value ? itemListEl.value.children : [];
        }

        onMounted(() => {
            if (itemListEl.value) {
                listHeight.value =
                    itemListEl.value.getBoundingClientRect().height;
            }
            isOpen.value = false;
        });

        onBeforeUnmount(() => {
            disableKeyboardControl();
        });

        watch(
            () => props.isDefaultOpen,
            (newValue: boolean) => {
                isOpen.value = newValue;
                scrollToHighlighted();
                disableKeyboardControl();

                if (newValue) {
                    enableKeyboardControl();
                    if (itemListEl.value) {
                        listHeight.value =
                            itemListEl.value.getBoundingClientRect().height;
                    }
                }
            }
        );

        return {
            currentHighlight,
            listHeight,
            isOpen,
            MAX_LIST_HEIGHT,
            itemListEl,
            click,
            enableKeyboardControl,
            disableKeyboardControl,
            scrollToHighlighted,
            setHighlighted,
            keyboardControl,
            getItemNodes,
        };
    },
});
</script>
